import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getModuleStats, formatDuration, getModuleStatus } from '../utils/testUtils';
// import { useTheme } from '../App';
import { Box, Flex, Stack, Input, Button, SimpleGrid, Text, useColorModeValue, Icon } from '@chakra-ui/react';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi';

function ModuleReports({ loading, selectedProject, runs }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  // const { dark } = useTheme();

  // Chakra color mode values (move all to top level)
  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const cardHoverBorder = 'green.400';
  const cardShadow = 'lg';
  const cardHoverShadow = 'xl';
  const cardBorderBottom = useColorModeValue('gray.100', 'gray.700');
  const cardTextColor = useColorModeValue('gray.600', 'gray.300');
  const cardTopBorder = useColorModeValue('gray.100', 'gray.700');
  const loadingTextColor = useColorModeValue('gray.600', 'gray.400');
  const noRunsTextColor = useColorModeValue('gray.600', 'gray.400');

  // Filter runs by selected project
const filteredRuns = useMemo(() => {
  if (!runs) return [];
  if (selectedProject && selectedProject !== 'ALL PROJECTS') {
    return runs.filter(r => r.project === selectedProject);
  }
  return runs;
}, [runs, selectedProject]);


  // Merge all testcases from all *filtered* runs for this project
  const allTestcases = useMemo(() => filteredRuns.flatMap(run => Array.isArray(run.testcases) ? run.testcases : []), [filteredRuns]);
  const modules = useMemo(() => {
    const grouped = {};
    allTestcases.forEach(tc => {
      const mod = tc.classname || 'Unknown';
      if (!grouped[mod]) grouped[mod] = [];
      grouped[mod].push(tc);
    });
    return grouped;
  }, [allTestcases]);
  const filteredModules = useMemo(() => Object.entries(modules).filter(([mod, cases]) => {
    const matchesSearch = mod.toLowerCase().includes(search.toLowerCase());
    const status = getModuleStatus(cases);
    const matchesFilter = filter === 'all' || (filter === 'pass' && status === 'pass') || (filter === 'fail' && status === 'fail');
    return matchesSearch && matchesFilter;
  }), [modules, search, filter]);

  // Debug logs
  // console.log('safeTestcases:', safeTestcases);
  // console.log('modules:', modules);
  // console.log('filteredModules:', filteredModules);

  // Virtualized row renderer - NOT directly used if mapping directly
  // const Row = ({ index, style }) => { ... };

  if (loading) {
    return <Flex align="center" justify="center" minH="60vh"><Text color={loadingTextColor}>Loading modules...</Text></Flex>;
  }

  // If not loading and no runs for the selected project, show message
  if (filteredRuns.length === 0) {
    return <Flex align="center" justify="center" minH="60vh"><Text color={noRunsTextColor}>No test runs found for the selected project.</Text></Flex>;
  }

  return (
    <Box w="100%" mt={8} px={{ base: 4, md: 8 }}>
      <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <Text fontSize="2xl" fontWeight="bold">Test Runs by Module</Text>
        <Flex gap={2} align="center">
          <Input
            type="search"
            placeholder="Search Modules"
            value={search}
            onChange={e => setSearch(e.target.value)}
            width={{ base: '100%', sm: '320px' }}
            size="lg"
            borderRadius="full"
            bg={inputBg}
            borderColor={inputBorder}
            _focus={{ borderColor: 'green.400', boxShadow: '0 0 0 1.5px #16a34a' }}
            fontSize="md"
            px={6}
            py={3}
          />
          <Button size="md" variant={filter === 'all' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setFilter('all')} borderRadius="lg">All</Button>
          <Button size="md" variant={filter === 'pass' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setFilter('pass')} borderRadius="lg">Passed</Button>
          <Button size="md" variant={filter === 'fail' ? 'solid' : 'outline'} colorScheme="green" onClick={() => setFilter('fail')} borderRadius="lg">Failed</Button>
        </Flex>
      </Flex>
      {filteredModules.length === 0 ? (
        <Box bg={cardBg} borderRadius="lg" p={8} textAlign="center" color={cardTextColor}>No modules found matching your criteria.</Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {filteredModules.map(([mod, cases]) => {
            const { passed, failed, skipped, total, percent, duration } = getModuleStats(cases);
            const moduleStatus = failed > 0 ? 'fail' : 'pass';
            // Extract only the filename (e.g., name.spec.js) from the module path
            const modFilename = mod.split(/[\\/]/).pop();
            return (
              <Box
                key={mod}
                bg={cardBg}
                borderRadius="2xl"
                p={6}
                boxShadow={cardShadow}
                borderWidth={1}
                borderColor={cardBorder}
                cursor="pointer"
                _hover={{ boxShadow: cardHoverShadow, borderColor: cardHoverBorder, transform: 'translateY(-2px) scale(1.01)' }}
                transition="all 0.18s"
                onClick={() => navigate(`/modules/${encodeURIComponent(mod)}`)}
                minH="210px"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
              >
                <Flex align="center" mb={4} borderBottomWidth={1} borderColor={cardBorderBottom} pb={3} gap={3}>
                  <Box w={10} h={10} borderRadius="full" bg={moduleStatus === 'pass' ? 'green.50' : 'red.50'} color={moduleStatus === 'pass' ? 'green.500' : 'red.500'} display="flex" alignItems="center" justifyContent="center" fontWeight="bold" fontSize="2xl">{moduleStatus === 'pass' ? <Icon as={FiCheckCircle} /> : <Icon as={FiXCircle} />}</Box>
                  <Text fontWeight="bold" fontSize="lg" flex={1} noOfLines={2}>{modFilename}</Text>
                  <Text fontSize="2xl" color={cardTextColor}>➔</Text>
                </Flex>
                <Stack spacing={1} fontSize="md" color={cardTextColor}> 
                  <Flex justify="space-between"><Text color="green.500">Passed:</Text> <Text fontWeight="bold">{passed}/{total} ({percent}%)</Text></Flex>
                  <Flex justify="space-between"><Text color="red.500">Failed:</Text> <Text fontWeight="bold">{failed}</Text></Flex>
                  <Flex justify="space-between"><Text color="yellow.600">Skipped:</Text> <Text fontWeight="bold">{skipped}</Text></Flex>
                  <Flex justify="space-between" mt={2} pt={2} borderTopWidth={1} borderColor={cardTopBorder}><Text>Duration:</Text> <Text fontWeight="bold">{formatDuration(duration)}</Text></Flex>
                </Stack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  );
}

export default ModuleReports; 